if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        form: 'form',
        fields: 'input[type=radio], select',
        swatch: '.swatch',
        price: '.product__price',
        submitBtn: 'form [type=submit]',
        linkBtn: '.js-pdp-link',
        cartDrawer: 'cart-drawer',
        mainImage: '.product__image-main',
        hoverImage: '.product__hover-image-variant img',
        preorderProperty: 'data-preorder-property',
        deliveryDateProperty: 'data-delivery-date-property',
      };
    }

    connectedCallback() {
      this.form = this.querySelector(this.selectors.form);
      this.fields = this.querySelectorAll(this.selectors.fields);
      this.swatches = this.querySelectorAll(this.selectors.swatch);
      this.cartDrawer = document.querySelector(
        this.selectors.cartDrawer
      );
      this.variants = JSON.parse(
        this.querySelector('[type="application/json"]').textContent
      );

      this.#updateOptions();
      this.#updateVariant();
      this.#setInitialImage();
      this.#changePrice(
        this.currentVariant?.price,
        this.currentVariant?.compare_at_price
      );
      if (!this.currentVariant?.available) {
        this.#disableSubmitButton();
      }
      this.#updateLinkBtn();

      this.#attachEvents();
    }

    /**
     * Attach events.
     */
    #attachEvents() {
      /**
       * Form submit.
       */
      this.form?.addEventListener('submit', (event) =>
        this.#handleSubmit(event)
      );

      /**
       * Color swatch events.
       */
      for (const swatch of this.swatches) {
        swatch.addEventListener('mouseover', (event) =>
          this.#changeImage(event)
        );
        swatch.addEventListener('mouseout', (event) =>
          this.#changeImage(event)
        );
      }

      /**
       * On option change.
       */
      for (const field of this.fields) {
        field.addEventListener('change', (event) =>
          this.#handleVariantChange(event)
        );
      }
    }

    /**
     * Handle form submit.
     *
     * @param {Event} event
     */
    async #handleSubmit(event) {
      event.preventDefault();

      this.#disableSubmitButton();

      const formData = new FormData(this.form);
      formData.append(
        'sections',
        this.cartDrawer
          .getSectionsToRender()
          .map((section) => section.section)
      );
      formData.append('sections_url', window.location.pathname);

      const responseData = await addToCart(formData);
      responseData && this.cartDrawer.renderContents(responseData);

      this.#enableSubmitButton();
    }

    /**
     * Handle variant change.
     *
     * @param {Event} event
     * @returns {Void}
     */
    #handleVariantChange(event) {
      if (event.target.type === 'number') return;
      this.#updateOptions();
      this.#updateVariant();
      this.#changeVariantImage(event.target.value);
      this.#updateLinkBtn();

      if (!this.currentVariant) {
        this.#changeButtonText(
          window.variantStrings.unavailable,
          false
        );
        this.#disableSubmitButton();
        return;
      }

      this.#updateVariantInput();
      this.#changePrice(
        this.currentVariant?.price,
        this.currentVariant?.compare_at_price
      );

      if (!this.currentVariant.available) {
        this.#changeButtonText(window.variantStrings.soldOut, false);
        this.#disableSubmitButton();
      } else {
        this.#enableSubmitButton();

        if (this.currentVariant.inventory_policy == 'continue' && this.currentVariant.inventory_quantity <= 0) {
          this.#changeButtonText(window.variantStrings.preOrderNow, true);
        } else {
          this.#changeButtonText(window.variantStrings.addToCart, true);
        }
      }

      this.#toggleProperties(this.currentVariant.inventory_policy == 'continue' && this.currentVariant.inventory_quantity <= 0);
    }

    #toggleProperties(added = true) {
      if (added) {
        const nextAvailableDate = this.currentVariant.next_available_date;

        const inputNote = document.createElement('input');

        inputNote.type = 'hidden';
        inputNote.name = `properties[Note]`;
        inputNote.value = window.cartStrings.preOrderDeliveryMessage.replace('[date]', nextAvailableDate);
        inputNote.setAttribute(this.selectors.preorderProperty, true);

        this.form.appendChild(inputNote);

        const inputDeliveryDate = document.createElement('input');

        inputDeliveryDate.type = 'hidden';
        inputDeliveryDate.name = `properties[_delivery_date]`;
        inputDeliveryDate.value = nextAvailableDate
        inputDeliveryDate.setAttribute(this.selectors.deliveryDateProperty, true);

        this.form.appendChild(inputDeliveryDate);
      } else {
        if (this.form.querySelector(`[${this.selectors.preorderProperty}]`)) {
          this.form.querySelector(`[${this.selectors.preorderProperty}]`).remove();
        }

        if (this.form.querySelector(`[${this.selectors.deliveryDateProperty}]`)) {
          this.form.querySelector(`[${this.selectors.deliveryDateProperty}]`).remove();
        }
      }
    }

    /**
     * Update currently selected options.
     */
    #updateOptions() {
      this.options = Array.from(
        this.querySelectorAll('input[type="radio"]:checked, select'),
        (el) => el.value
      );
    }

    /**
     * Update currentVariant property.
     */
    #updateVariant() {
      this.currentVariant = this.variants.find((variant) => {
        return !variant.options
          .map((option, index) => this.options[index] === option)
          .includes(false);
      });
    }

    /**
     * Update variant input id.
     */
    #updateVariantInput() {
      const input = this.querySelector('[name="id"]');
      input.value = this.currentVariant.id;
    }

    /**
     * Change current image based on change event.
     *
     * @param {Event} event
     * @returns {Void}
     */
    #changeImage(event) {
      const variantColor = event.target.getAttribute('title');

      const image = this.querySelector(`[color="${variantColor}"]`);
      if (!image) return;

      image.classList.toggle('visible');

      if (event.type == 'mouseover') {
        // If the color is already selected, do nothing.
        if (image.classList.contains('selected')) return;

        // Hide the currently selected image so the image of the hovered color becomes visible.
        if (this.querySelector('img.selected')) {
          this.querySelector('img.selected').classList.add(
            'transparent'
          );
        }
      }

      if (event.type == 'mouseout') {
        if (this.querySelector('img.selected')) {
          this.querySelectorAll(
            '.product__hover-image-variant img'
          ).forEach((image) => {
            image.classList.remove('transparent');
          });
        }
      }
    }

    /**
     * Change price text.
     *
     * @param {Number} price
     * @param {Number} compareAtPrice
     */
    #changePrice(price, compareAtPrice) {
      if (!price) return
      const mainPrice = this.querySelector(this.selectors.price);

      mainPrice &&
        (mainPrice.innerHTML = `
        <ins>${Shopify.formatMoney(
          price,
          Shopify.current_money_format
        )}</ins>
        ${
          !!compareAtPrice
            ? `<del>${Shopify.formatMoney(
                compareAtPrice,
                Shopify.current_money_format
              )}</del>`
            : ''
        }
      `);
    }

    /**
     * Disable submit button.
     */
    #disableSubmitButton() {
      this.querySelector(this.selectors.submitBtn)?.setAttribute(
        'disabled',
        true
      );
      this.querySelector(this.selectors.submitBtn)?.classList.add(
        'disabled'
      );
    }

    /**
     * Enable submit button.
     */
    #enableSubmitButton() {
      this.querySelector(this.selectors.submitBtn)?.removeAttribute(
        'disabled'
      );
      this.querySelector(this.selectors.submitBtn)?.classList.remove(
        'disabled'
      );
    }

    /**
     * Change submit button text.
     *
     * @param {String} text
     * @returns {Void}
     */
    #changeButtonText(text) {
      const button = this.querySelector(this.selectors.submitBtn);
      if (!button) return;
      button.innerHTML = `${text}`;
    }

    /**
     * Change image based on color.
     *
     * @param {String} color
     */
    #changeVariantImage(color) {
      this.querySelectorAll(this.selectors.hoverImage).forEach(
        (image) => {
          if (image.getAttribute('color') === color) {
            image.classList.add('selected');
          } else {
            image.classList.remove('selected');
            image.classList.remove('transparent');
          }
        }
      );
      this.querySelector(this.selectors.mainImage)?.classList.add(
        'transparent'
      );
    }

    #setInitialImage() {
      const color = Array.from(this.swatches).find(
        (swatch) => swatch.previousElementSibling.checked
      )?.previousElementSibling?.value;

      this.#changeVariantImage(color);
    }

    #updateLinkBtn() {
      const link = this.querySelector(this.selectors.linkBtn);
      if (!link) return;

      const currentVariantId = link.getAttribute('href')?.split('variant=')[1]?.split('&')[0];
      const newLink = link.getAttribute('href')?.replace(currentVariantId, this.currentVariant.id);

      link.setAttribute('href', newLink);
    }
  }
  customElements.define('product-card', ProductCard);
}
